'use client';

import { useState, useEffect, useMemo } from 'react';
import {
  LayoutGrid,
  ChefHat,
  Receipt,
  Settings,
  Utensils,
  Wifi,
  WifiOff,
  Clock,
  User,
  LogOut,
  Activity,
  Table2,
  Printer,
  DollarSign,
  Users,
  ArrowLeft,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { User as PosUser } from '@/lib/pos-types';

export type AdminTab = 'dashboard' | 'salon' | 'inventory' | 'menu' | 'printers' | 'activity' | 'cashier' | 'users' | 'clients';

const adminNavItems = [
  { id: 'dashboard' as AdminTab, label: 'Dashboard', icon: Activity },
  { id: 'salon'     as AdminTab, label: 'Salón',     icon: Table2 },
  { id: 'inventory' as AdminTab, label: 'Inventario', icon: Package },
  { id: 'menu'      as AdminTab, label: 'Carta',     icon: Utensils },
  { id: 'printers'  as AdminTab, label: 'Impresoras', icon: Printer },
  { id: 'activity'  as AdminTab, label: 'Actividad', icon: Clock },
  { id: 'cashier'   as AdminTab, label: 'Caja',      icon: DollarSign },
  { id: 'users'     as AdminTab, label: 'Staff',     icon: Users },
  { id: 'clients'   as AdminTab, label: 'Clientes',  icon: DollarSign },
];

interface POSLayoutProps {
  children: React.ReactNode;
  activeView: 'tables' | 'kitchen' | 'bar' | 'cashier' | 'admin';
  onViewChange: (view: 'tables' | 'kitchen' | 'bar' | 'cashier' | 'admin') => void;
  currentUser: PosUser;
  apiOnline?: boolean;
  onRetryApi?: () => void;
  onLogout?: () => void;
  adminTab?: AdminTab;
  onAdminTabChange?: (tab: AdminTab) => void;
}

const navItems = [
  { id: 'tables' as const, label: 'Mesas', icon: LayoutGrid },
  { id: 'kitchen' as const, label: 'Cocina', icon: ChefHat },
  { id: 'bar' as const, label: 'Bar', icon: ChefHat },
  { id: 'cashier' as const, label: 'Caja', icon: Receipt },
  { id: 'admin' as const, label: 'Admin', icon: Settings },
];

export function POSLayout({ children, activeView, onViewChange, currentUser, apiOnline, onRetryApi, onLogout, adminTab = 'dashboard', onAdminTabChange }: POSLayoutProps) {
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [networkOnline, setNetworkOnline] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => setNetworkOnline(true);
    const handleOffline = () => setNetworkOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const effectiveOnline = networkOnline && (apiOnline ?? true);
  const connectionState = useMemo(() => {
    if (!networkOnline) return 'offline';
    if (apiOnline === false) return 'api-down';
    return 'online';
  }, [networkOnline, apiOnline]);
  const allowedViews = useMemo(() => {
    switch (currentUser.role) {
      case 'waiter':
        return ['tables'] as const;
      case 'kitchen':
        return ['kitchen', 'bar'] as const;
      case 'cashier':
        return ['cashier'] as const;
      case 'admin':
        return ['tables', 'kitchen', 'bar', 'cashier', 'admin'] as const;
      case 'auditor':
        return ['admin'] as const;
      default:
        return ['tables'] as const;
    }
  }, [currentUser.role]);
  const visibleNavItems = useMemo(
    () => navItems.filter(i => (allowedViews as readonly string[]).includes(i.id)),
    [allowedViews]
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar - Desktop Only */}
      <aside className="hidden sm:flex w-20 flex-col bg-sidebar border-r border-sidebar-border shrink-0">
        {/* Logo */}
        <div className="flex items-center justify-center h-16 border-b border-sidebar-border">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary">
            <Utensils className="w-5 h-5 text-primary-foreground" />
          </div>
        </div>

        {activeView === 'admin' ? (
          /* Admin Tab Navigation */
          <nav className="flex-1 flex flex-col items-center gap-1 py-3 overflow-y-auto no-scrollbar">
            {/* Back button */}
            {currentUser.role !== 'auditor' && (
              <>
                <button
                  onClick={() => onViewChange('tables')}
                  className={cn(
                    'flex flex-col items-center justify-center w-14 h-10 rounded-xl transition-all duration-200 mb-1',
                    'text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                  )}
                  title="Volver a Mesas"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="w-8 border-t border-sidebar-border mb-1" />
              </>
            )}
            {adminNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onAdminTabChange?.(item.id)}
                className={cn(
                  'flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all duration-200',
                  'hover:bg-sidebar-accent',
                  adminTab === item.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-sidebar-foreground/60 hover:text-sidebar-foreground'
                )}
              >
                <item.icon className="w-5 h-5 mb-1" />
                <span className="text-[10px] font-medium leading-tight text-center">{item.label}</span>
              </button>
            ))}
          </nav>
        ) : (
          /* Main Navigation */
          <nav className="flex-1 flex flex-col items-center gap-2 py-4">
            {visibleNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={cn(
                  'flex flex-col items-center justify-center w-14 h-14 rounded-xl transition-all duration-200',
                  'hover:bg-sidebar-accent',
                  activeView === item.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-sidebar-foreground/60 hover:text-sidebar-foreground'
                )}
              >
                <item.icon className="w-5 h-5 mb-1" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        )}

        {/* User Avatar */}
        <div className="flex items-center justify-center py-4 border-t border-sidebar-border">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Header */}
        <header className="h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 bg-card border-b border-border shrink-0">
          <div className="flex items-center gap-3 sm:gap-4 overflow-hidden">
            <div className="flex sm:hidden items-center justify-center w-8 h-8 rounded-lg bg-primary shrink-0">
              <Utensils className="w-4 h-4 text-primary-foreground" />
            </div>
            <h1 className="text-base sm:text-lg font-bold text-foreground truncate whitespace-nowrap">
              {activeView === 'admin'
                ? adminNavItems.find(i => i.id === adminTab)?.label ?? 'Admin'
                : navItems.find(i => i.id === activeView)?.label}
            </h1>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-foreground truncate max-w-[60px] sm:max-w-[100px]">{currentUser.name}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-6">
            {/* Connection Status - Icon only on mobile */}
            <div className={cn(
              'flex items-center gap-2 sm:px-3 sm:py-1.5 rounded-lg text-sm',
              connectionState === 'online'
                ? 'bg-emerald-500/10 text-emerald-400'
                : connectionState === 'api-down'
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-red-500/10 text-red-400',
              'px-2 py-1'
            )}>
              {connectionState === 'online' ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span className="hidden sm:inline">
                {connectionState === 'online'
                  ? 'Conectado'
                  : connectionState === 'api-down'
                    ? 'API sin conexión'
                    : 'Sin internet'}
              </span>
            </div>

            {!effectiveOnline && connectionState === 'api-down' && onRetryApi && (
              <button
                type="button"
                onClick={onRetryApi}
                className="hidden sm:inline-flex items-center justify-center h-8 px-3 rounded-lg border border-border bg-card hover:bg-muted text-xs font-bold text-foreground"
              >
                Reintentar
              </button>
            )}

            {/* Time */}
            <div className="flex items-center gap-2 text-foreground">
              <Clock className="hidden sm:block w-4 h-4 text-muted-foreground" />
              <span className="font-mono text-xs sm:text-sm font-bold bg-muted sm:bg-transparent px-2 py-1 rounded sm:p-0">
                {currentTime ? currentTime.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
              </span>
            </div>

            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                className="inline-flex items-center justify-center h-8 px-3 rounded-lg border border-border bg-card hover:bg-muted text-xs font-bold text-foreground"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            )}
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-4 sm:p-6 pb-24 sm:pb-6">
          {children}
        </main>

        {/* Bottom Navigation - Mobile Only */}
        <nav className="sm:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t border-border flex items-center justify-around px-2 z-40">
          {visibleNavItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full transition-all duration-200',
                activeView === item.id
                  ? 'text-primary'
                  : 'text-muted-foreground'
              )}
            >
              <item.icon className={cn("w-5 h-5 mb-1", activeView === item.id && "animate-bounce-subtle")} />
              <span className="text-[10px] font-bold">{item.label}</span>
              {activeView === item.id && (
                <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
